import {
  BenefitCategory,
  BenefitsFinancialImpactCalculationDto,
  FinancialParametersCalculationDto,
  PBData,
  PersonAgeType,
  SingleBenefitFinancialImpactCalculationDto,
} from '@flexyoo/api';
import {
  EmployeeCartBenefit,
  EmployeeSelectedBenefit,
  IFinancialImpactBenefitDetails,
  IFinancialImpactBenefitFundingView,
  IFinancialImpactBenefitView,
  IFinancialImpactComparisonRowView,
  IFinancialImpactFundingSourceView,
  IFinancialImpactPackageRowView,
  IFinancialImpactReportView,
  IFinancialImpactSalarySacrificeView,
} from '@flexyoo/employee/models';

/**
 * Amount fields of the parameters DTO. Confirmed with the backend: every amount
 * is annual and arrives positive (`MonthlyGrossSalary` is the single monthly
 * value), so the report decides which lines are rendered as "(Minus) …".
 */
type FinancialParameterAmountField = Exclude<keyof FinancialParametersCalculationDto, 'HasPB'>;

type ComparisonRowDefinition = {
  key: string;
  /** Summed when the report shows several DTO fields as a single line. */
  fields: FinancialParameterAmountField[];
  /** Defaults to `employee.financial-impact-report.rows.<key>`. */
  labelKey?: string;
  isSubtracted?: boolean;
  isHighlighted?: boolean;
};

/**
 * Order of the "Financial impact" table. The spec makes the sequence mandatory,
 * so the list is deliberately explicit instead of derived from the DTO.
 *
 * The funding lines borrow their labels from `funding.report-title` so that a
 * funding type is named the same way here and in the "Budget types" section, and
 * Bonus+ / Bonus- are summed into one line exactly as they are there.
 *
 * The end-of-year premium line prints what the backend PDF of this report does:
 * `EndOfYearPremiumNet` for an employee with an end-of-year premium funding, and
 * otherwise `EndOfYearPremiumFromSS`, the premium the backend derives from the
 * monthly gross salary. The backend fills only one of the two, so they are summed.
 *
 * The Primes & Bonuses line prints the net amounts (`PB…VacationPayNet`), the same
 * ones as the "Budget types" row, not the gross `PB…VacationPay` budget.
 */
const COMPARISON_ROWS: ComparisonRowDefinition[] = [
  { key: 'gross-salary', fields: ['AnnualGrossSalary'] },
  {
    key: 'end-of-year-premium',
    labelKey: 'funding.report-title.end-of-year-premium',
    fields: ['EndOfYearPremiumNet', 'EndOfYearPremiumFromSS'],
  },
  { key: 'holiday-pay', fields: ['HolidayPay'] },
  {
    key: 'premiums-and-bonuses',
    labelKey: 'funding.report-title.premiums-and-bonuses',
    fields: ['PBWithVacationPayNet', 'PBWithoutVacationPayNet'],
  },
  {
    key: 'hr-calculated-budget',
    labelKey: 'funding.report-title.hr-calculated-budget',
    fields: ['PBSetByHr'],
  },
  { key: 'total-gross-compensation', fields: ['TotalGrossCompensation'], isHighlighted: true },
  { key: 'social-security', fields: ['SSContribution'], isSubtracted: true },
  { key: 'taxable-income', fields: ['TaxableIncome'], isHighlighted: true },
  { key: 'withholding-tax', fields: ['WithholdingTax'], isSubtracted: true },
  { key: 'net-salary', fields: ['NetSalary'], isHighlighted: true },
];

/** Facts shown under the benefit name in the "Selected benefits" section. */
const buildBenefitDetails = (
  basketBenefit: EmployeeCartBenefit | undefined
): IFinancialImpactBenefitDetails[] => {
  if (!basketBenefit) {
    return [];
  }

  if (basketBenefit.Category === BenefitCategory.AdditionalLeave) {
    return basketBenefit.QuantityOfLeaveDays
      ? [{ key: 'days', params: { count: basketBenefit.QuantityOfLeaveDays } }]
      : [];
  }

  const insuredPersons = basketBenefit.InsuredPersons ?? [];
  const countOf = (ageType: PersonAgeType): number =>
    insuredPersons.filter((person) => person.OptionAgeType === ageType).length;

  // only the groups that are actually insured, e.g. "Children: 1" alone
  return [
    { key: 'adults', params: { count: countOf(PersonAgeType.Adult) } },
    { key: 'children', params: { count: countOf(PersonAgeType.Child) } },
  ].filter((detail) => detail.params.count > 0);
};

const sumMonthlySalarySacrifice = (
  benefits: SingleBenefitFinancialImpactCalculationDto[]
): number =>
  benefits.reduce(
    (total, benefit) =>
      total + (benefit.HasSalarySacrifice ? benefit.MonthlySalarySacrificeAmount : 0),
    0
  );

/**
 * 50477: the long-term benefits of earlier choice periods that are already paid and
 * still run next year (`LongtermPaid`) keep lowering the salary, so the calculation
 * counts them next to the reported selection. The backend sends one consolidated
 * sacrifice for both; it is split here the way its PDF of the report does, by
 * summing the benefits of each kind.
 */
const buildSalarySacrifice = (
  impact: BenefitsFinancialImpactCalculationDto
): IFinancialImpactSalarySacrificeView | null => {
  const benefitsImpact = impact.BenefitsImpact ?? [];
  const monthlySacrificeAmount = sumMonthlySalarySacrifice(
    benefitsImpact.filter((benefit) => !benefit.LongtermPaid)
  );
  const existingMonthlySacrificeAmount = sumMonthlySalarySacrifice(
    benefitsImpact.filter((benefit) => benefit.LongtermPaid)
  );

  // already rounded by the backend, unlike a sum of the two parts
  const totalMonthlySacrificeAmount = impact.ConsolidatedImpact?.MonthlySalarySacrificeAmount ?? 0;

  if (!totalMonthlySacrificeAmount) {
    return null;
  }

  return {
    totalMonthlySacrificeAmount,
    monthlySacrificeAmount,
    existingMonthlySacrificeAmount,
    initialMonthlyGrossSalary: impact.FinancialParametersBefore?.MonthlyGrossSalary ?? 0,
    newMonthlyGrossSalary: impact.FinancialParametersAfter?.MonthlyGrossSalary ?? 0,
  };
};

const sumPrimesAndBonuses = (
  amounts: FinancialParametersCalculationDto | PBData | undefined
): number =>
  (amounts?.PBWithoutVacationPay ?? 0) +
  (amounts?.PBWithVacationPay ?? 0) +
  (amounts?.PBSetByHr ?? 0);

const sumPrimesAndBonusesNet = (
  parameters: FinancialParametersCalculationDto | undefined
): number =>
  (parameters?.PBWithoutVacationPayNet ?? 0) +
  (parameters?.PBWithVacationPayNet ?? 0) +
  (parameters?.PBSetByHrNet ?? 0);

/**
 * "Budget types" rows, computed exactly like the backend PDF of the report
 * (`FinancialImpactReportPdfGenerator`): from the same calculation as the rest of
 * the report, so the spent amount covers the reported benefits (in cart mode, the
 * current cart) and the dialog always shows the figures of the downloaded PDF.
 *
 * Column pairing follows the employee budget page: the backend "net" amounts are
 * what the UI calls gross, its "gross" amounts are the budget.
 */
const buildFundingSources = (
  impact: BenefitsFinancialImpactCalculationDto
): IFinancialImpactFundingSourceView[] => {
  const before = impact.FinancialParametersBefore;
  const after = impact.FinancialParametersAfter;
  const consumed = impact.ConsolidatedImpact?.PBGrossAmounts;

  if (!before?.HasPB) {
    return [];
  }

  const sources: IFinancialImpactFundingSourceView[] = [];
  const assignedPrimesAndBonusesBudget = sumPrimesAndBonuses(before);

  if (assignedPrimesAndBonusesBudget > 0) {
    sources.push({
      translationKey: 'premiums-and-bonuses',
      assignedGrossAmount: sumPrimesAndBonusesNet(before),
      assignedBudgetAmount: assignedPrimesAndBonusesBudget,
      spentBudgetAmount: sumPrimesAndBonuses(consumed),
      remainingBudgetAmount: sumPrimesAndBonuses(after),
      remainingGrossAmount: sumPrimesAndBonusesNet(after),
    });
  }

  if (before.EndOfYearPremium > 0) {
    sources.push({
      translationKey: 'end-of-year-premium',
      assignedGrossAmount: before.EndOfYearPremiumNet,
      assignedBudgetAmount: before.EndOfYearPremium,
      spentBudgetAmount: consumed?.P13 ?? 0,
      remainingBudgetAmount: after?.EndOfYearPremium ?? 0,
      remainingGrossAmount: after?.EndOfYearPremiumNet ?? 0,
    });
  }

  return sources;
};

/** `TotalGrossAmountSpent` of the backend PDF of the report. */
const sumSpentGrossAmount = (sources: IFinancialImpactFundingSourceView[]): number =>
  sources.reduce(
    (total, source) => total + source.assignedGrossAmount - source.remainingGrossAmount,
    0
  );

/**
 * 51344: a benefit funded by both families (salary sacrifice and Primes & Bonuses)
 * lists each of them on a line of its own, salary sacrifice first like the PDF;
 * a benefit with neither has no funding line.
 *
 * 51741: the Primes & Bonuses line shows the gross amount of the employee budget page, which
 * the backend sends as `ConsumedAmount` (`PBNetAmounts` stays empty); `PBGrossAmounts` is the
 * budget behind it and tells whether the benefit is paid from Primes & Bonuses at all.
 */
const buildBenefitFundings = (
  impact: SingleBenefitFinancialImpactCalculationDto | undefined
): IFinancialImpactBenefitFundingView[] => {
  const fundings: IFinancialImpactBenefitFundingView[] = [];
  const salarySacrificeAmount = impact?.MonthlySalarySacrificeAmount ?? 0;
  const primesAndBonusesAmount = impact?.PBGrossAmounts?.Total ? (impact.ConsumedAmount ?? 0) : 0;

  if (salarySacrificeAmount > 0) {
    fundings.push({ family: 'salary-sacrifice', amount: salarySacrificeAmount });
  }

  if (primesAndBonusesAmount > 0) {
    fundings.push({ family: 'primes-and-bonuses', amount: primesAndBonusesAmount });
  }

  return fundings;
};

/**
 * The name and the category of a benefit come with the calculation itself, so a benefit
 * the employee's portfolio no longer holds (e.g. one whose flex end date has passed)
 * is reported just like the PDF does. The portfolio only adds what the calculation
 * does not carry: the details under the name and the amount the employee asked for.
 */
const buildBenefit = (
  benefitImpact: SingleBenefitFinancialImpactCalculationDto,
  employeeBenefit: EmployeeSelectedBenefit | undefined,
  basketBenefit: EmployeeCartBenefit | undefined
): IFinancialImpactBenefitView => ({
  benefitId: benefitImpact.BenefitId,
  name: benefitImpact.Name ?? employeeBenefit?.name ?? '',
  category: {
    NameEn: benefitImpact.CategoryNameEn ?? employeeBenefit?.categoryNameEn ?? '',
    NameFr: benefitImpact.CategoryNameFr ?? employeeBenefit?.categoryNameFr ?? '',
    NameNl: benefitImpact.CategoryNameNl ?? employeeBenefit?.categoryNameNl ?? '',
  },
  details: buildBenefitDetails(basketBenefit),
  // the amount the employee typed in; benefits without one fall back to the
  // yearly net price, which `requestedAmount` of the cart holds
  annualAmount:
    basketBenefit?.RequestedAmount ??
    employeeBenefit?.requestedAmount ??
    benefitImpact.RequestedAmount,
  // amount the calculation used for the benefit, matching the PDF package table
  totalAmount: benefitImpact.RequestedAmount,
  fundings: buildBenefitFundings(benefitImpact),
  isLongTermPaid: !!benefitImpact.LongtermPaid,
});

/**
 * The benefits the backend calculated the report for, in the order of its PDF:
 * those with an impact first, then those without one. The backend decides which
 * benefits belong in the report (e.g. it leaves out benefits HR rejected and adds
 * the approved ones still running outside the cart), so every one of them is listed.
 */
const buildBenefits = (
  impact: BenefitsFinancialImpactCalculationDto,
  employeeBenefits: EmployeeSelectedBenefit[],
  basketBenefits: EmployeeCartBenefit[]
): IFinancialImpactBenefitView[] => {
  const employeeBenefitById = new Map(employeeBenefits.map((benefit) => [benefit.id, benefit]));
  const basketBenefitById = new Map(basketBenefits.map((benefit) => [benefit.Id, benefit]));

  return [...(impact.BenefitsImpact ?? []), ...(impact.BenefitsWithNoImpact ?? [])].map(
    (benefitImpact) =>
      buildBenefit(
        benefitImpact,
        employeeBenefitById.get(benefitImpact.BenefitId),
        basketBenefitById.get(benefitImpact.BenefitId)
      )
  );
};

const sumFields = (
  parameters: FinancialParametersCalculationDto | undefined,
  fields: FinancialParameterAmountField[]
): number => fields.reduce((total, field) => total + (parameters?.[field] ?? 0), 0);

/**
 * The amounts of "(Minus) …" rows are negated here, so the minus sign is part of
 * the number ("-6.969,00 €") rather than a prefix of it. A negative amount from
 * the backend therefore shows up positive, a visible sign the contract is broken.
 */
const buildComparisonRows = (
  impact: BenefitsFinancialImpactCalculationDto
): IFinancialImpactComparisonRowView[] => {
  const before = impact.FinancialParametersBefore;
  const after = impact.FinancialParametersAfter;

  return COMPARISON_ROWS.map((definition) => {
    const sign = definition.isSubtracted ? -1 : 1;

    return {
      key: definition.key,
      labelKey: definition.labelKey ?? `employee.financial-impact-report.rows.${definition.key}`,
      before: sign * sumFields(before, definition.fields),
      after: sign * sumFields(after, definition.fields),
      isSubtracted: !!definition.isSubtracted,
      isHighlighted: !!definition.isHighlighted,
    };
  }).filter((row) => row.isHighlighted || row.before !== 0 || row.after !== 0);
};

/**
 * Every selected benefit with the amount requested for it, like the
 * "Benefits funded through salary package conversion" table of the backend PDF.
 */
const buildPackageRows = (
  benefits: IFinancialImpactBenefitView[]
): IFinancialImpactPackageRowView[] =>
  benefits.map(({ benefitId, category, totalAmount }) => ({
    benefitId,
    category,
    value: totalAmount,
  }));

/** Assembles everything the report renders from the API responses and the employee's benefits. */
export const buildFinancialImpactReportView = (
  impact: BenefitsFinancialImpactCalculationDto,
  employeeBenefits: EmployeeSelectedBenefit[],
  basketBenefits: EmployeeCartBenefit[]
): IFinancialImpactReportView => {
  const after = impact.FinancialParametersAfter;
  const allBenefits = buildBenefits(impact, employeeBenefits, basketBenefits);
  const packageRows = buildPackageRows(allBenefits);
  const fundingSources = buildFundingSources(impact);

  return {
    salarySacrifice: buildSalarySacrifice(impact),
    fundingSources,
    fundingSourcesSpentGrossAmount: sumSpentGrossAmount(fundingSources),
    benefits: allBenefits.filter((benefit) => !benefit.isLongTermPaid),
    longTermBenefits: allBenefits.filter((benefit) => benefit.isLongTermPaid),
    comparisonRows: buildComparisonRows(impact),
    // the backend reports how much the net salary drops (net before minus net
    // after); the report shows the change itself, so a drop reads as negative
    annualNetPackageImpact: after?.AnnualNetImpact ? -after.AnnualNetImpact : 0,
    packageRows,
    packageTotalValue: packageRows.reduce((total, row) => total + row.value, 0),
  };
};
